import React from "react";
import { lowerCase, map } from "lodash";
import { useTranslation } from "react-i18next";
import { SUBMISSION_CATEGORY } from "common/constants";
import { useGetResidentCustomObjectSubmissionsQuery } from "../../../generated/graphql";
import useNavigationTab from "../../../hooks/useNavigationTabs";
import { getOptionalTabs } from "../../../utils/submissions";
import ListItem from "../../AddressPanel/Submissions/ListItem";
import { EmptyState } from "../../Common/EmptyState";
import TabGroup from "../../Common/TabGroup";
import {
  TabContainer,
  TabGroupContainer,
} from "../Property/__styles__/PropertyPage";

export type RecordsTabProps = {
  geometryId: string;
  categories: Set<SUBMISSION_CATEGORY>;
};

const SubmissionCategoryDisplay = ({
  geometryId,
  category,
  title,
  titleWithinCopy,
}: {
  geometryId: string;
  category: SUBMISSION_CATEGORY;
  title: string;
  titleWithinCopy: string;
}) => {
  const { t } = useTranslation();

  const { data, loading, error } = useGetResidentCustomObjectSubmissionsQuery({
    variables: { geometryId, category },
    fetchPolicy: "cache-and-network",
  });

  if (loading) {
    return <em>{t("common-loading")}</em>;
  }

  if (error) {
    return (
      <span>
        {t("property-record-loading-error", { title: lowerCase(title) })}
      </span>
    );
  }

  const submissions = data?.customMapGeometry?.submissions;
  if (!submissions?.length) {
    return (
      <EmptyState
        icon="add-file"
        title={t("property-no-record-header", {
          title: lowerCase(titleWithinCopy),
        })}
        body={t("property-no-record-subtext", {
          title: lowerCase(titleWithinCopy),
        })}
      />
    );
  }

  return (
    <>
      {submissions.map(submission => (
        <ListItem
          key={submission.id}
          submission={submission}
          onUpdate={() => {}}
          objectIsHiddenFromPublic={false}
        />
      ))}
    </>
  );
};

export const Records = ({ geometryId, categories }: RecordsTabProps) => {
  const { t } = useTranslation();

  const optionalTabs = getOptionalTabs(t);
  const tabs = optionalTabs
    .filter(({ category }) => categories.has(category))
    .map(({ recordTabName, title }) => ({
      value: recordTabName,
      label: title,
    }));

  const defaultTab = tabs[0];

  const [tab, setTab] = useNavigationTab({
    defaultTab: defaultTab?.value,
    allowedTabs: map(tabs, "value"),
    queryParamName: "recordTab",
  });

  if (!tab) {
    return null;
  }

  const currentOptionalTab = optionalTabs.find(
    ({ recordTabName }) => tab === recordTabName,
  );

  return (
    <>
      <TabGroupContainer data-testid="record-tabs" hasOutlineStyleActiveTabs>
        <TabGroup
          currentTab={tab}
          setTab={setTab}
          tabs={tabs}
          activeTabStyle="outline"
        />
      </TabGroupContainer>
      <TabContainer>
        {currentOptionalTab && (
          <SubmissionCategoryDisplay
            geometryId={geometryId}
            category={currentOptionalTab.category}
            title={currentOptionalTab.title}
            titleWithinCopy={currentOptionalTab.titleWithinCopy}
          />
        )}
      </TabContainer>
    </>
  );
};
